import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import LanguageDropdown from "../Components/Common/LanguageDropdown";
import BrandLogoLinks from "../Components/Common/BrandLogoLinks";
import FullScreenDropdown from "../Components/Common/FullScreenDropdown";
import LightDark from "../Components/Common/LightDark";

import { changeSidebarVisibility } from "../slices/thunks";
import {
  clearSuperAdminSession,
  getLoggedinSuperAdmin,
  saUrl,
  APIClient,
} from "../helpers/api_helper";
import { getInitials } from "../helpers/userDisplay";
import {
  readAlwaysIconsOnly,
  subscribeAlwaysIconsOnly,
  toggleAlwaysIconsOnly,
} from "../helpers/twoColumnAlwaysIcons";

const api = new APIClient();

/** Avatar circular fixo — evita “pular” layout com iniciais de 1 vs 2 letras. */
const avatarStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  minWidth: 32,
  fontSize: 12,
  lineHeight: "32px",
};

/**
 * Header do shell SA — sem NotificationDropdown, cart, apps ou busca demo (DOC 09 § 4).
 * LAC-190: mesmo toggle “só ícones” do tenant (localStorage por admin).
 */
const SuperAdminHeader = ({
  onChangeLayoutMode,
  layoutModeType,
  headerClass,
}: any) => {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const [alwaysIcons, setAlwaysIcons] = useState(() =>
    readAlwaysIconsOnly("sa")
  );

  useEffect(() => {
    return subscribeAlwaysIconsOnly(() => {
      setAlwaysIcons(readAlwaysIconsOnly("sa"));
    });
  }, []);

  const selectLayout = createSelector(
    (state: any) => state.Layout,
    (layout) => layout.sidebarVisibilitytype
  );
  const sidebarVisibilitytype = useSelector(selectLayout);

  const session = getLoggedinSuperAdmin();
  const displayName = session?.user?.name || session?.user?.email || t("sa.header.admin");
  const initials = getInitials(displayName, "SA");

  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const toogleMenuBtn = () => {
    const windowSize = document.documentElement.clientWidth;
    const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement;
    dispatch(changeSidebarVisibility("show"));

    if (windowSize > 767) {
      humberIcon?.classList.toggle("open");
    }

    if (sidebarVisibilitytype === "show") {
      if (windowSize < 1025 && windowSize > 767) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.getAttribute("data-sidebar-size") === "sm"
          ? document.documentElement.setAttribute("data-sidebar-size", "")
          : document.documentElement.setAttribute("data-sidebar-size", "sm");
      } else if (windowSize > 1025) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.getAttribute("data-sidebar-size") === "lg"
          ? document.documentElement.setAttribute("data-sidebar-size", "sm")
          : document.documentElement.setAttribute("data-sidebar-size", "lg");
      } else if (windowSize <= 767) {
        document.body.classList.add("vertical-sidebar-enable");
        document.documentElement.setAttribute("data-sidebar-size", "lg");
      }
    }

    if (document.documentElement.getAttribute("data-layout") === "twocolumn") {
      document.body.classList.contains("twocolumn-panel")
        ? document.body.classList.remove("twocolumn-panel")
        : document.body.classList.add("twocolumn-panel");
    }
  };

  const handleLogout = async () => {
    try {
      await api.create(saUrl("/super-admin/logout"), {});
    } catch {
      // Mesmo se a API falhar, limpa sessão local.
    }
    clearSuperAdminSession();
    window.location.href = "/super-admin/login";
  };

  return (
    <header id="page-topbar" className={headerClass}>
      <div className="layout-width">
        <div className="navbar-header">
          <div className="d-flex">
            <BrandLogoLinks to="/super-admin/dashboard" />

            <button
              onClick={toogleMenuBtn}
              type="button"
              className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger"
              id="topnav-hamburger-icon"
              title={
                alwaysIcons
                  ? t("shell.twoColumn.hamburgerAlwaysOn")
                  : t("shell.twoColumn.hamburger")
              }
              aria-label={
                alwaysIcons
                  ? t("shell.twoColumn.hamburgerAlwaysOn")
                  : t("shell.twoColumn.hamburger")
              }
            >
              <span className="hamburger-icon">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>

            <button
              type="button"
              className={`btn btn-icon btn-topbar btn-ghost-secondary rounded-circle d-none d-md-inline-flex ${
                alwaysIcons ? "text-primary" : ""
              }`}
              onClick={() => toggleAlwaysIconsOnly("sa")}
              title={
                alwaysIcons
                  ? t("shell.twoColumn.alwaysIconsOn")
                  : t("shell.twoColumn.alwaysIconsOff")
              }
              aria-label={
                alwaysIcons
                  ? t("shell.twoColumn.alwaysIconsOn")
                  : t("shell.twoColumn.alwaysIconsOff")
              }
              aria-pressed={alwaysIcons}
            >
              <i className="bx bx-sidebar fs-22" aria-hidden="true"></i>
            </button>
          </div>

          <div className="d-flex align-items-center">
            <LanguageDropdown />
            <FullScreenDropdown />
            <LightDark
              layoutMode={layoutModeType}
              onChangeLayoutMode={onChangeLayoutMode}
            />

            {/* Sem ? de ajuda pública no SA — ajuda. é só cliente; ops → _DOCS/02. */}

            <Dropdown
              isOpen={isProfileOpen}
              toggle={() => setIsProfileOpen(!isProfileOpen)}
              className="ms-sm-3 header-item topbar-user"
            >
              <DropdownToggle tag="button" type="button" className="btn">
                <span className="d-flex align-items-center">
                  <span
                    className="rounded-circle header-profile-user bg-primary text-dark d-inline-flex align-items-center justify-content-center fw-semibold"
                    style={avatarStyle}
                    aria-hidden="true"
                  >
                    {initials}
                  </span>
                  <span className="text-start ms-xl-2">
                    <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
                      {displayName}
                    </span>
                    <span className="d-none d-xl-block ms-1 fs-12 text-muted user-name-sub-text">
                      {t("sa.header.role")}
                    </span>
                  </span>
                </span>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <h6 className="dropdown-header">
                  {t("sa.header.welcome", { name: displayName })}
                </h6>
                <DropdownItem
                  className="p-0"
                  tag={Link}
                  to="/super-admin/account"
                >
                  <span className="dropdown-item">
                    <i className="mdi mdi-account-cog text-muted fs-16 align-middle me-1"></i>
                    <span className="align-middle">{t("sa.header.account")}</span>
                  </span>
                </DropdownItem>
                <DropdownItem className="p-0" onClick={handleLogout}>
                  <span className="dropdown-item" role="button">
                    <i className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i>
                    <span className="align-middle">{t("sa.header.logout")}</span>
                  </span>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>
    </header>
  );
};

export default SuperAdminHeader;
